<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">
        {{ editing ? "Editar viaje" : "Nuevo viaje" }}
      </h1>
    </div>
    <div>
      <form class="custom-form" @submit.prevent="save" novalidate>

        <b-card tag="article" no-body class="custom-card" style="max-width: 900px">
          <b-card-header header-tag="section" class="custom-card-header d-flex align-items-center">
            <h3>{{ editing ? "Datos del viaje" : "Datos del nuevo viaje" }}</h3>
          </b-card-header>
          <b-card-body class="custom-card-body p-0">
            <section class="form-section">

              <!-- ═══════════ SECCIÓN 1: Pasajero ═══════════ -->
              <div>
                <h2 class="form-subtitle">Datos del pasajero</h2>
              </div>
              <div class="row">

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="celular">Celular *</label>
                    <FormInput id="celular" type="text" v-model.trim="form.celular" autocomplete="off"
                      placeholder="Ingresar celular (solo números)"
                      :show-error="$v.form.celular.$error && !$v.form.celular.required"
                      @input="form.celular = $event.replace(/[^0-9]/g, '')" />
                    <FormError v-if="$v.form.celular.$error && !$v.form.celular.required"
                      message="El celular es requerido" />
                    <span v-if="searchingPhone" class="field-hint">Buscando pasajero...</span>
                    <span v-if="passengerDetected" class="field-hint field-hint--success">Pasajero encontrado: {{ passengerDetected.full_name }}</span>
                    <span v-if="phoneSearched && !passengerDetected && !searchingPhone" class="field-hint field-hint--muted">No se encontró pasajero registrado</span>
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="nombres">Nombres *</label>
                    <FormInput id="nombres" type="text" v-model.trim="form.nombres" autocomplete="off"
                      placeholder="Ingresar nombres"
                      :show-error="$v.form.nombres.$error && !$v.form.nombres.required" />
                    <FormError v-if="$v.form.nombres.$error && !$v.form.nombres.required"
                      message="Los nombres son requeridos" />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="apellidos">Apellidos *</label>
                    <FormInput id="apellidos" type="text" v-model.trim="form.apellidos" autocomplete="off"
                      placeholder="Ingresar apellidos"
                      :show-error="$v.form.apellidos.$error && !$v.form.apellidos.required" />
                    <FormError v-if="$v.form.apellidos.$error && !$v.form.apellidos.required"
                      message="Los apellidos son requeridos" />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="tipo_pasajero">Tipo de pasajero *</label>
                    <FormSelect id="tipo_pasajero" v-model="form.tipo_pasajero" :items="tipoPasajeroOptions"
                      defaultOption="Elegir tipo"
                      :showError="$v.form.tipo_pasajero.$error && !$v.form.tipo_pasajero.required" />
                    <FormError v-if="$v.form.tipo_pasajero.$error && !$v.form.tipo_pasajero.required"
                      message="El tipo de pasajero es requerido" />
                    <span v-if="passengerTypeDetected" class="field-hint field-hint--success">Detectado: {{ passengerTypeLabel }}</span>
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="cant_pax">Cant. pasajeros *</label>
                    <FormInput id="cant_pax" type="number" min="1" v-model.number="form.cant_pax" autocomplete="off"
                      :show-error="$v.form.cant_pax.$error && !$v.form.cant_pax.required" />
                    <FormError v-if="$v.form.cant_pax.$error && !$v.form.cant_pax.required"
                      message="La cantidad de pasajeros es requerida" />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="cant_maletas">Cant. maletas *</label>
                    <FormInput id="cant_maletas" type="number" min="0" v-model.number="form.cant_maletas"
                      autocomplete="off"
                      :show-error="$v.form.cant_maletas.$error && !$v.form.cant_maletas.required" />
                    <FormError v-if="$v.form.cant_maletas.$error && !$v.form.cant_maletas.required"
                      message="La cantidad de maletas es requerida" />
                  </div>
                </div>

                <div class="col-md-12" v-if="showAutoRegister">
                  <div class="form-group">
                    <label class="checkbox-label">
                      <input type="checkbox" v-model="form.auto_registrado" />
                      Registrar automaticamente como pasajero nuevo
                    </label>
                  </div>
                </div>

              </div>

              <!-- ═══════════ SECCIÓN 2: Datos del viaje ═══════════ -->
              <div>
                <h2 class="form-subtitle">Datos del viaje</h2>
              </div>
              <div class="row">

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="fecha">Fecha *</label>
                    <FormInput id="fecha" type="text" placeholder="DD/MM/AAAA" v-model="form.fecha"
                      v-mask="'##/##/####'"
                      :show-error="$v.form.fecha.$error && !$v.form.fecha.required" />
                    <FormError v-if="$v.form.fecha.$error && !$v.form.fecha.required"
                      message="La fecha es requerida" />
                    <span v-if="isPastDate" class="past-date-warning">Estas registrando un viaje con fecha pasada</span>
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="hora">Hora *</label>
                    <FormInput id="hora" type="text" placeholder="HH:MM" v-model="form.hora"
                      v-mask="'##:##'"
                      :show-error="$v.form.hora.$error && !$v.form.hora.required" />
                    <FormError v-if="$v.form.hora.$error && !$v.form.hora.required"
                      message="La hora es requerida" />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="fuente">Fuente *</label>
                    <select id="fuente" v-model="selectedSourceKey" class="form-select"
                      :class="{ 'is-error': $v.form.fuente.$error && !$v.form.fuente.required }"
                      @change="onSourceChange">
                      <option value="">Elegir fuente</option>
                      <optgroup label="── Directo ──">
                        <option v-for="s in bookingSources.fixed" :key="'f-'+s.value" :value="'DIRECT:'+s.value">{{ s.label }}</option>
                      </optgroup>
                      <optgroup label="── OTAs ──">
                        <option v-for="s in bookingSources.otas" :key="'o-'+s.value" :value="'OTA:'+s.value+':'+s.id">{{ s.label }}</option>
                      </optgroup>
                      <optgroup label="── Afiliados ──">
                        <option v-for="s in bookingSources.affiliates" :key="'a-'+s.id" :value="'AFFILIATE:'+s.value+':'+s.id">{{ s.label }}</option>
                      </optgroup>
                    </select>
                    <FormError v-if="$v.form.fuente.$error && !$v.form.fuente.required"
                      message="La fuente es requerida" />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="channel">Canal</label>
                    <FormSelect id="channel" v-model="form.channel" :items="channelOptions"
                      defaultOption="Elegir canal" />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="tipo_viaje">Tipo de viaje *</label>
                    <FormSelect id="tipo_viaje" v-model="form.tipo_viaje" :items="tipoViajeOptions"
                      defaultOption="Elegir tipo de viaje"
                      :showError="$v.form.tipo_viaje.$error && !$v.form.tipo_viaje.required" />
                    <FormError v-if="$v.form.tipo_viaje.$error && !$v.form.tipo_viaje.required"
                      message="El tipo de viaje es requerido" />
                  </div>
                </div>

                <!-- Origen -->
                <div class="col-md-4">
                  <div class="form-group">
                    <label for="origen">Origen *</label>
                    <FormInput v-if="origenReadonly" id="origen" type="text" :value="form.origen" disabled />
                    <FormInput v-else id="origen" type="text" v-model.trim="form.origen" autocomplete="off"
                      placeholder="Ingresar origen"
                      :show-error="$v.form.origen.$error && !$v.form.origen.required" />
                    <FormError v-if="!origenReadonly && $v.form.origen.$error && !$v.form.origen.required"
                      message="El origen es requerido" />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="distrito_origen">Distrito origen</label>
                    <FormSelect v-if="!distritoOrigenReadonly" id="distrito_origen" v-model="form.distrito_origen"
                      :items="districtOptions" defaultOption="Elegir distrito" />
                    <FormInput v-else id="distrito_origen" type="text" :value="form.distrito_origen" disabled />
                  </div>
                </div>

                <!-- Destino -->
                <div class="col-md-4">
                  <div class="form-group">
                    <label for="destino">Destino *</label>
                    <FormInput v-if="destinoReadonly" id="destino" type="text" :value="form.destino" disabled />
                    <FormInput v-else id="destino" type="text" v-model.trim="form.destino" autocomplete="off"
                      placeholder="Ingresar destino"
                      :show-error="$v.form.destino.$error && !$v.form.destino.required" />
                    <FormError v-if="!destinoReadonly && $v.form.destino.$error && !$v.form.destino.required"
                      message="El destino es requerido" />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="distrito_destino">Distrito destino</label>
                    <FormSelect v-if="!distritoDestinoReadonly" id="distrito_destino" v-model="form.distrito_destino"
                      :items="districtOptions" defaultOption="Elegir distrito" />
                    <FormInput v-else id="distrito_destino" type="text" :value="form.distrito_destino" disabled />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="tipo_vuelo">Tipo de vuelo</label>
                    <FormSelect id="tipo_vuelo" v-model="form.tipo_vuelo" :items="tipoVueloOptions"
                      defaultOption="Elegir tipo de vuelo" />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="num_vuelo">Numero de vuelo</label>
                    <FormInput id="num_vuelo" type="text" v-model.trim="form.num_vuelo" autocomplete="off"
                      placeholder="Ej: LA2045" />
                  </div>
                </div>

                <div class="col-md-12">
                  <div class="form-group">
                    <label for="observaciones">Observaciones</label>
                    <textarea id="observaciones" v-model.trim="form.observaciones" rows="3"
                      placeholder="Ingresar observaciones" class="custom-form-control"></textarea>
                  </div>
                </div>

                <div class="col-md-12" v-if="editing && currentEstado === 'NON_SHOW'">
                  <div class="form-group">
                    <label for="motivo_non_show">Motivo Non Show</label>
                    <textarea id="motivo_non_show" v-model.trim="form.motivo_non_show" rows="2"
                      placeholder="Ingresar motivo del non show" class="custom-form-control"></textarea>
                  </div>
                </div>

              </div>

              <!-- ═══════════ SECCIÓN 3: Servicio y precio ═══════════ -->
              <div>
                <h2 class="form-subtitle">Servicio y precio</h2>
              </div>
              <div class="row">

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="tipo_vehiculo">Tipo de vehiculo *</label>
                    <FormSelect id="tipo_vehiculo" v-model="form.tipo_vehiculo" :items="tipoVehiculoOptions"
                      defaultOption="Elegir tipo de vehiculo"
                      :showError="$v.form.tipo_vehiculo.$error && !$v.form.tipo_vehiculo.required" />
                    <FormError v-if="$v.form.tipo_vehiculo.$error && !$v.form.tipo_vehiculo.required"
                      message="El tipo de vehiculo es requerido" />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="moneda">Moneda *</label>
                    <FormSelect id="moneda" v-model="form.moneda" :items="monedaOptions"
                      defaultOption="Elegir moneda"
                      :showError="$v.form.moneda.$error && !$v.form.moneda.required" />
                    <FormError v-if="$v.form.moneda.$error && !$v.form.moneda.required"
                      message="La moneda es requerida" />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="precio">Precio *</label>
                    <FormInput id="precio" type="number" min="0" step="0.01" v-model.number="form.precio"
                      autocomplete="off"
                      :show-error="$v.form.precio.$error && !$v.form.precio.required" />
                    <FormError v-if="$v.form.precio.$error && !$v.form.precio.required"
                      message="El precio es requerido" />
                  </div>
                </div>

                <div class="col-md-12" v-if="calculatedTariff !== null">
                  <div class="tariff-info">
                    Tarifa calculada: {{ calculatedTariffLabel }} = S/ {{ calculatedTariff.toFixed(2) }}
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="cupon_code">Cupon de descuento</label>
                    <FormInput id="cupon_code" type="text" v-model.trim="form.cupon_code" autocomplete="off"
                      placeholder="Ej: VERANO20" />
                    <span v-if="validatingCoupon" class="field-hint">Validando cupon...</span>
                    <span v-if="couponResult && couponResult.valid" class="field-hint field-hint--success">
                      Descuento: {{ couponResult.discount_type === 'percent' ? couponResult.discount_value + '%' : 'S/ ' + couponResult.discount_value }} (-S/ {{ couponResult.discount_amount.toFixed(2) }})
                    </span>
                    <span v-if="couponResult && !couponResult.valid" class="field-hint field-hint--error">
                      {{ couponResult.message }}
                    </span>
                  </div>
                </div>

                <!-- Desglose de precio con cupón -->
                <div class="col-md-12" v-if="couponResult && couponResult.valid && form.precio">
                  <div class="price-breakdown">
                    <div class="price-breakdown__row">
                      <span>Precio original:</span>
                      <span>{{ monedaSymbol }} {{ parseFloat(form.precio).toFixed(2) }}</span>
                    </div>
                    <div class="price-breakdown__row price-breakdown__row--discount">
                      <span>Descuento ({{ couponResult.discount_type === 'percent' ? couponResult.discount_value + '%' : monedaSymbol + ' ' + couponResult.discount_value }} — {{ form.cupon_code }}):</span>
                      <span>-{{ monedaSymbol }} {{ parseFloat(form.descuento_aplicado).toFixed(2) }}</span>
                    </div>
                    <div class="price-breakdown__row price-breakdown__row--total">
                      <span>Precio final:</span>
                      <span>{{ monedaSymbol }} {{ computedPrecioFinal }}</span>
                    </div>
                  </div>
                </div>

              </div>

              <!-- ═══════════ SECCIÓN 4: Identificación ═══════════ -->
              <div>
                <h2 class="form-subtitle">Identificacion</h2>
              </div>
              <div class="row">

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="ride">Ride <span v-if="!isOtaFuente" class="text-muted">(opcional)</span><span v-else> *</span></label>
                    <FormInput id="ride" type="text" v-model.trim="form.ride" autocomplete="off"
                      placeholder="Ej: POL-24512 (vacio = auto)"
                      :show-error="$v.form.ride.$error" />
                    <FormError v-if="$v.form.ride.$error"
                      message="El ride es obligatorio para esta fuente" />
                  </div>
                </div>

              </div>

              <!-- Botones -->
              <div class="row">
                <div class="col-md-12">
                  <div class="form-group-buttons text-right">
                    <router-link :to="{ name: 'viajes-ride-list' }" class="button button-light">
                      {{ editing ? "Volver" : "Cancelar" }}
                    </router-link>
                    <button class="button button-primary" type="submit">
                      Guardar
                    </button>
                  </div>
                </div>
              </div>

            </section>
          </b-card-body>
        </b-card>

      </form>
    </div>
  </div>
</template>

<script>
import { Alert, FormSelect, FormError, FormInput } from 'wize-admin'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { RideService, PassengerService, CouponService, BasePriceService } from '@/core/services'

const PASSENGER_TYPE_MAP = {
  1: 'Ocasional',
  2: 'Regular',
  3: 'QORE',
  4: 'FF',
  5: 'Corporativo'
}

export default {
  components: { FormError, FormSelect, FormInput },
  data() {
    return {
      editing: false,
      // Búsqueda de pasajero
      searchingPhone: false,
      phoneSearched: false,
      passengerDetected: null,
      passengerTypeDetected: false,
      phoneDebounceTimer: null,
      // Cupón
      validatingCoupon: false,
      couponResult: null,
      couponDebounceTimer: null,
      // Tarifa calculada
      calculatedPrices: [],
      calculatedTariff: null,
      calculatedTariffLabel: '',
      // Distritos
      districts: [],
      // Fuentes dinámicas
      bookingSources: { fixed: [], otas: [], affiliates: [] },
      selectedSourceKey: '',
      // Opciones
      tipoViajeOptions: [
        { key: 'APTO-HOT',  label: 'APTO-HOT' },
        { key: 'HOT-APTO',  label: 'HOT-APTO' },
        { key: 'DISTRITAL', label: 'DISTRITAL' }
      ],
      tipoVueloOptions: [
        { key: 'NACIONAL', label: 'Nacional' },
        { key: 'INTER',    label: 'Internacional' }
      ],
      tipoVehiculoOptions: [
        { key: 'SEDAN',    label: 'Sedan' },
        { key: 'SUV',      label: 'SUV' },
        { key: 'VAN',      label: 'Van' },
        { key: 'SPRINTER', label: 'Sprinter' }
      ],
      monedaOptions: [
        { key: 'SOLES',   label: 'Soles (PEN)' },
        { key: 'DOLARES', label: 'Dolares (USD)' }
      ],
      tipoPasajeroOptions: [
        { key: 'Ocasional',    label: 'Ocasional' },
        { key: 'Regular',      label: 'Regular' },
        { key: 'QORE',         label: 'QORE' },
        { key: 'FF',           label: 'FF' },
        { key: 'Corporativo',  label: 'Corporativo' }
      ],
      channelOptions: [
        { key: 'WHATSAPP',  label: 'WhatsApp' },
        { key: 'FACEBOOK',  label: 'Facebook' },
        { key: 'INSTAGRAM', label: 'Instagram' },
        { key: 'EMAIL',     label: 'Correo electrónico' },
        { key: 'PHONE',     label: 'Teléfono' },
        { key: 'WEB',       label: 'Página web' }
      ],
      form: {
        ride:              null,
        fecha:             null,
        hora:              null,
        fuente:            null,
        tipo_viaje:        null,
        origen:            null,
        destino:           null,
        tipo_vuelo:        null,
        num_vuelo:         null,
        observaciones:     null,
        nombres:           null,
        apellidos:         null,
        celular:           null,
        cant_pax:          null,
        cant_maletas:      null,
        tipo_vehiculo:     null,
        precio:            null,
        moneda:            null,
        distrito_origen:   null,
        distrito_destino:  null,
        tipo_pasajero:     null,
        cupon_code:        null,
        descuento_aplicado: null,
        precio_final:      null,
        auto_registrado:   false,
        source_type:       'DIRECT',
        affiliate_id:      null,
        channel:           null,
        motivo_non_show:   null
      },
      currentEstado:       null
    }
  },
  validations: {
    form: {
      ride:          { required: requiredIf(function () { return this.isOtaFuente }) },
      fecha:         { required },
      hora:          { required },
      fuente:        { required },
      tipo_viaje:    { required },
      origen:        { required },
      destino:       { required },
      nombres:       { required },
      apellidos:     { required },
      celular:       { required },
      cant_pax:      { required },
      cant_maletas:  { required },
      tipo_vehiculo: { required },
      precio:        { required },
      moneda:        { required },
      tipo_pasajero: { required }
    }
  },
  computed: {
    isPastDate() {
      if (this.editing || !this.form.fecha) return false
      const parts = this.form.fecha.split('/')
      if (parts.length !== 3 || this.form.fecha.length !== 10) return false
      const [day, month, year] = parts.map(Number)
      const selected = new Date(year, month - 1, day)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return selected < today
    },
    districtOptions() {
      return this.districts.map(d => ({ key: d.name, label: d.name }))
    },
    origenReadonly() {
      return this.form.tipo_viaje === 'APTO-HOT'
    },
    destinoReadonly() {
      return this.form.tipo_viaje === 'HOT-APTO'
    },
    distritoOrigenReadonly() {
      return this.form.tipo_viaje === 'APTO-HOT'
    },
    distritoDestinoReadonly() {
      return this.form.tipo_viaje === 'HOT-APTO'
    },
    showAutoRegister() {
      return !this.editing && this.phoneSearched && !this.passengerDetected
    },
    passengerTypeLabel() {
      return this.form.tipo_pasajero || ''
    },
    isOtaFuente() {
      return this.form.source_type === 'OTA'
    },
    monedaSymbol() {
      return this.form.moneda === 'DOLARES' ? 'US$' : 'S/'
    },
    computedPrecioFinal() {
      const precio = parseFloat(this.form.precio) || 0
      const descuento = parseFloat(this.form.descuento_aplicado) || 0
      return Math.max(0, precio - descuento).toFixed(2)
    },
    relevantDistrict() {
      if (this.form.tipo_viaje === 'HOT-APTO' || this.form.tipo_viaje === 'DISTRITAL') {
        return this.form.distrito_origen
      }
      if (this.form.tipo_viaje === 'APTO-HOT') {
        return this.form.distrito_destino
      }
      return null
    }
  },
  watch: {
    '$route.params.id': function () {
      if (this.$route.params.id) this.load(this.$route.params.id)
    },
    'form.source_type': function (val) {
      if (val === 'OTA') {
        this.form.moneda = 'DOLARES'
      }
    },
    'form.celular': function (val) {
      if (this.editing) return
      clearTimeout(this.phoneDebounceTimer)
      this.passengerDetected = null
      this.passengerTypeDetected = false
      this.phoneSearched = false
      if (!val || val.length < 9) return
      this.phoneDebounceTimer = setTimeout(() => {
        this.searchPassenger(val)
      }, 400)
    },
    'form.tipo_viaje': function (val) {
      if (val === 'APTO-HOT') {
        this.form.origen = 'Aeropuerto Jorge Chavez'
        this.form.distrito_origen = 'Callao'
        this.form.destino = this.editing ? this.form.destino : null
        this.form.distrito_destino = this.editing ? this.form.distrito_destino : null
      } else if (val === 'HOT-APTO') {
        this.form.destino = 'Aeropuerto Jorge Chavez'
        this.form.distrito_destino = 'Callao'
        this.form.origen = this.editing ? this.form.origen : null
        this.form.distrito_origen = this.editing ? this.form.distrito_origen : null
      }
      this.loadCalculatedPrices()
    },
    'form.tipo_vehiculo': function () { this.recalcTariff() },
    'form.tipo_pasajero': function () { this.recalcTariff() },
    relevantDistrict() { this.recalcTariff() },
    'form.cupon_code': function (val) {
      clearTimeout(this.couponDebounceTimer)
      this.couponResult = null
      if (!val || val.length < 3) {
        this.form.descuento_aplicado = null
        this.form.precio_final = null
        return
      }
      this.couponDebounceTimer = setTimeout(() => {
        this.validateCoupon(val)
      }, 400)
    }
  },
  methods: {
    // ── Buscar pasajero por teléfono ────────────────────────────────
    async searchPassenger(phone) {
      this.searchingPhone = true
      try {
        const response = await PassengerService.getByPhone(phone)
        const p = response.payload
        this.passengerDetected = p
        this.phoneSearched = true
        // Autocompletar campos
        if (p.full_name && p.full_name !== '-') {
          const parts = p.full_name.split(' ')
          if (parts.length >= 2) {
            this.form.nombres = parts.slice(0, Math.ceil(parts.length / 2)).join(' ')
            this.form.apellidos = parts.slice(Math.ceil(parts.length / 2)).join(' ')
          } else {
            this.form.nombres = p.full_name
          }
        }
        // Mapear tipo de pasajero
        const typeLabel = PASSENGER_TYPE_MAP[p.passenger_type]
        if (typeLabel) {
          this.form.tipo_pasajero = typeLabel
          this.passengerTypeDetected = true
        }
        this.form.auto_registrado = false
      } catch {
        this.passengerDetected = null
        this.phoneSearched = true
        this.form.auto_registrado = false
      } finally {
        this.searchingPhone = false
      }
    },

    // ── Tarifa calculada ────────────────────────────────────────────
    async loadCalculatedPrices() {
      const tv = this.form.tipo_viaje
      if (!tv) { this.calculatedPrices = []; return }
      let routeType = 'district_to_airport'
      if (tv === 'APTO-HOT') routeType = 'airport_to_district'
      try {
        const response = await BasePriceService.getCalculatedPrices(routeType)
        this.calculatedPrices = response.payload || []
      } catch {
        this.calculatedPrices = []
      }
      this.recalcTariff()
    },
    recalcTariff() {
      this.calculatedTariff = null
      this.calculatedTariffLabel = ''
      const district = this.relevantDistrict
      const vehicle = this.form.tipo_vehiculo
      const paxType = this.form.tipo_pasajero
      if (!district || !vehicle || !paxType || this.calculatedPrices.length === 0) return

      // Buscar fila por distrito y vehículo
      const vehicleMap = { SEDAN: 'Sedan', SUV: 'SUV', VAN: 'VAN', SPRINTER: 'Sprinter' }
      const vehicleName = vehicleMap[vehicle] || vehicle
      const row = this.calculatedPrices.find(
        r => r.district_name === district && r.vehicle_type === vehicleName
      )
      if (!row || !row.prices || row.prices.length === 0) return

      // Buscar precio por tipo de pasajero
      const price = row.prices.find(p =>
        p.price_type_name && p.price_type_name.toLowerCase().includes(paxType.toLowerCase())
      )
      if (price) {
        this.calculatedTariff = price.calculated_amount
        this.calculatedTariffLabel = `${district} + ${vehicleName} + ${paxType}`
        if (!this.isOtaFuente) {
          this.form.precio = price.calculated_amount
        } else {
          this.form.precio = null
        }
      } else {
        this.form.precio = null
      }
    },

    // ── Validar cupón ───────────────────────────────────────────────
    async validateCoupon(code) {
      this.validatingCoupon = true
      try {
        const amount = this.form.precio || 0
        const response = await CouponService.validate(code, amount)
        this.couponResult = response.payload
        if (this.couponResult && this.couponResult.valid) {
          this.form.descuento_aplicado = this.couponResult.discount_amount
          this.form.precio_final = Math.max(0, (this.form.precio || 0) - this.couponResult.discount_amount)
        }
      } catch {
        this.couponResult = { valid: false, message: 'Error al validar el cupon' }
      } finally {
        this.validatingCoupon = false
      }
    },

    // ── Guardar ─────────────────────────────────────────────────────
    async save() {
      this.$v.$touch()
      if (this.$v.$error) {
        this.$nextTick(() => {
          const el = document.querySelector('.form-error')
          if (el) {
            this.$smoothScroll({ scrollTo: el, updateHistory: false, offset: -50 })
          }
        })
        return
      }

      try {
        await this.$store.dispatch('app/loading', true)
        const data = { ...this.form }

        // Convertir fecha de DD/MM/YYYY a YYYY-MM-DD para el backend
        if (data.fecha && data.fecha.length === 10) {
          const [day, month, year] = data.fecha.split('/')
          data.fecha = `${year}-${month}-${day}`
        }

        if (!this.editing) {
          await RideService.create(data)
          Alert.success('Se almaceno la informacion correctamente')
          this.$router.push({ name: 'viajes-ride-list' })
        } else {
          await RideService.update(this.$route.params.id, data)
          Alert.success('Se almaceno la informacion correctamente')
          await this.load(this.$route.params.id)
        }
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al guardar')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },

    // ── Cargar para edición ─────────────────────────────────────────
    async load(id) {
      try {
        await this.$store.dispatch('app/loading', true)
        const response = await RideService.getById(id)
        const p = response.payload

        this.form = {
          ride:               p.ride,
          fecha:              p.fecha ? p.fecha.substring(0, 10).split('-').reverse().join('/') : null,
          hora:               p.hora ? p.hora.substring(0, 5) : null,
          fuente:             p.fuente,
          tipo_viaje:         p.tipo_viaje,
          origen:             p.origen,
          destino:            p.destino,
          tipo_vuelo:         p.tipo_vuelo || null,
          num_vuelo:          p.num_vuelo || null,
          observaciones:      p.observaciones || null,
          nombres:            p.nombres,
          apellidos:          p.apellidos,
          celular:            p.celular,
          cant_pax:           p.cant_pax,
          cant_maletas:       p.cant_maletas,
          tipo_vehiculo:      p.tipo_vehiculo,
          precio:             parseFloat(p.precio),
          moneda:             p.moneda,
          distrito_origen:    p.distrito_origen || null,
          distrito_destino:   p.distrito_destino || null,
          tipo_pasajero:      p.tipo_pasajero || null,
          cupon_code:         p.cupon_code || null,
          descuento_aplicado: p.descuento_aplicado ? parseFloat(p.descuento_aplicado) : null,
          precio_final:       p.precio_final ? parseFloat(p.precio_final) : null,
          auto_registrado:    p.auto_registrado || false,
          source_type:        p.source_type || 'DIRECT',
          affiliate_id:       p.affiliate_id || null,
          channel:            p.channel || null,
          motivo_non_show:    p.motivo_non_show || null
        }
        this.currentEstado = p.estado || null
        this.selectedSourceKey = this.buildSourceKey(this.form.source_type, this.form.fuente, this.form.affiliate_id)
        this.editing = true
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar')
        this.$router.push({ name: 'viajes-ride-list' })
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },

    // ── Fuentes dinámicas ──────────────────────────────────────────
    async loadBookingSources() {
      try {
        const response = await RideService.getBookingSources()
        this.bookingSources = response.payload || { fixed: [], otas: [], affiliates: [] }
      } catch {
        this.bookingSources = { fixed: [], otas: [], affiliates: [] }
      }
    },
    onSourceChange() {
      const key = this.selectedSourceKey
      if (!key) {
        this.form.fuente = null
        this.form.source_type = 'DIRECT'
        this.form.affiliate_id = null
        return
      }
      const parts = key.split(':')
      const type = parts[0]
      const value = parts[1]
      this.form.fuente = value
      this.form.source_type = type
      this.form.affiliate_id = type === 'AFFILIATE' ? parseInt(parts[2]) : null
      if (type === 'OTA') {
        this.form.precio = null
      }
      // Si la fuente es FF, sincronizar tipo_pasajero automáticamente
      if (this.form.fuente === 'FF') {
        this.form.tipo_pasajero = 'FF'
      } else if (this.form.fuente !== 'FF' && this.form.tipo_pasajero === 'FF') {
        // Si cambia de FF a otra fuente, resetear tipo_pasajero
        this.form.tipo_pasajero = 'Ocasional'
      }
    },
    buildSourceKey(sourceType, fuente, affiliateId) {
      if (sourceType === 'AFFILIATE' && affiliateId) return `AFFILIATE:${fuente}:${affiliateId}`
      if (sourceType === 'OTA') {
        const ota = this.bookingSources.otas.find(o => o.value === fuente)
        return ota ? `OTA:${fuente}:${ota.id}` : `OTA:${fuente}:`
      }
      return `DIRECT:${fuente}`
    },

    // ── Cargar distritos ────────────────────────────────────────────
    async loadDistricts() {
      try {
        const response = await BasePriceService.getDistricts()
        this.districts = response.payload || []
      } catch {
        this.districts = []
      }
    }
  },
  async created() {
    await Promise.all([this.loadDistricts(), this.loadBookingSources()])
    if (this.$route.params.id) {
      await this.load(this.$route.params.id)
    }
  }
}
</script>

<style lang="stylus" scoped>
.past-date-warning
  display block
  margin-top 4px
  color #e67e22
  font-size 13px
  font-weight normal

.field-hint
  display block
  margin-top 4px
  font-size 12px
  color #6c757d

  &--success
    color #27ae60

  &--muted
    color #95a5a6

  &--error
    color #e74c3c

.tariff-info
  background #f0f7ff
  border-left 3px solid #3498db
  padding 8px 12px
  margin-bottom 16px
  font-size 13px
  color #2c3e50
  border-radius 0 4px 4px 0

.price-breakdown
  background #f8fff8
  border-left 3px solid #27ae60
  padding 10px 14px
  margin-bottom 16px
  border-radius 0 4px 4px 0
  &__row
    display flex
    justify-content space-between
    font-size 13px
    padding 2px 0
    &--discount
      color #e74c3c
    &--total
      font-weight bold
      font-size 14px
      border-top 1px solid #ddd
      padding-top 6px
      margin-top 4px
      color #27ae60

select.is-error
  border-color #dc3545

.checkbox-label
  display flex
  align-items center
  gap 8px
  font-weight normal
  cursor pointer
  input[type="checkbox"]
    margin 0
</style>
